import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { FilterChangedEvent, GridApi } from "ag-grid-community";
import "ag-grid-enterprise";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";
import axios from "axios";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { preBorrowColdDefs } from "../../common/locateColumnDefs";
import { constants, EndPoint, socketMsgType } from "../../common/constants";
import { convertToCommas, getRowStyle } from "../../common/commonFunctions";
import {
  BASE_URI_SOCKET,
  envs,
  getEnv,
  serviceAPIMapping,
} from "../../config/service-api-mapping";
import { validateTermDate } from "../../common/tradeValidations";
import SpinnerFullScreen from "../SpinnerFullScreen";
import SockJS from "sockjs-client";
import { Stomp } from "@stomp/stompjs";
// import Queue from "yocto-queue";

interface AgGridProps {
  title?: string;
  onClickHan?: any;
  onClickSelectData?: any;
  onCellChange?: any;
  seTtotalSelectedRows?: any;
  getBtnRef?: any;
  getGridApi?: any;
  doesExternalFilterPass?: any;
  isExternalFilterPresent?: any;
  isDeselectAll?: boolean;
  getContextMenuItems?: any;
  globalSearch?: any;
  isSingleRowInserted?: boolean;
  defaultFilter?: any;
  isPreBorrowsRefreshed?: boolean;
  onRowSelect?: any;
  date: any;
  newRows?: any;
  isAdmin?: boolean;
  globalClient?: string;
  enableSocketUpdates?: boolean;
  currentInterval?: number;
  onRefreshLocateKPI?:()=> void
}

const PreBorrowGrid = ({
  title,
  getGridApi,
  doesExternalFilterPass,
  isExternalFilterPresent,
  isDeselectAll,
  getContextMenuItems,
  globalSearch,
  defaultFilter,
  isPreBorrowsRefreshed,
  getBtnRef,
  onRowSelect,
  date,
  newRows,
  isAdmin,
  globalClient,
  enableSocketUpdates,
  currentInterval,
  onRefreshLocateKPI
}: AgGridProps) => {
  const gridRef = useRef<any>(null);
  const theme = useTheme();
  const [preBorrowRows, setPreBorrowRows] = useState<any>([]);
  const [initialPreBorrowRows, setInitialPreBorrowRows] = useState<any>([]);
  const [columnDefs] = useState<any>([...preBorrowColdDefs]);
  const [showScreenLoader, setShowScreenLoader] = useState<boolean>(false);
  const activeClientShortName = window.localStorage.getItem(
    "activeClientShortName"
  );
  const loginUser = window.localStorage.getItem(
    "username"
  );

  const [gridTheme, setGridTheme] = useState<string>(
    theme.palette.mode === "dark" ? " ag-theme-balham-dark" : " ag-theme-balham"
  );
  const socketConsolidate = new SockJS(`${BASE_URI_SOCKET()}`);
  const socketConsolidateClient = Stomp.over(function () {
    return socketConsolidate;
  });
  const [insertPreBorrowRows, setInsertPreborrowRows] = useState<any>([]);

  // const queue = new Queue();

  useEffect(() => {
    setGridTheme(
      theme.palette.mode === "dark"
        ? " ag-theme-balham-dark"
        : " ag-theme-balham"
    );
  }, [theme.palette.mode]);

  let gridApi: any;
  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: false,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        const isTabKey = params?.event?.key === constants.Tab;
        if (isTabKey) gridRef?.current?.api?.stopEditing();

        return false;
      },
    };
  }, []);

  useEffect(() => {
    if (defaultFilter && gridRef) {
      const filterComponent = gridRef?.current?.api?.getFilterInstance(
        defaultFilter.column
      );
      if (filterComponent) {
        filterComponent?.setModel({ values: defaultFilter.values });
        gridApi.onFilterChanged();
      }
    }
  }, [gridRef]);

  useEffect(() => {
    if (date) {
      getPreBorrows();
    }
  }, [isPreBorrowsRefreshed, date]);

  useEffect(() => {
    if (newRows && _.isArray(newRows) && newRows?.length > 0) {
      // setPreBorrowRows((previousRows: any) => {
      //   return [...newRows, ...previousRows]
      // })

      setInitialPreBorrowRows((previousRows: any) => {
        return [...newRows, ...previousRows]
      })
      onRefreshLocateKPI?.()
    }
  }, [newRows])

  const getPreBorrows = async () => {
    try {
      setShowScreenLoader(true);
      setPreBorrowRows([]);
      setInitialPreBorrowRows([]);
      const accountId = window.localStorage.getItem("activeAccountCode");
      const request = isAdmin
        ? {
            date: moment(date).format("YYYY-MM-DD"),
            account: accountId,
          }
        : {
            date: moment(date).format("YYYY-MM-DD"),
            account: accountId,
            clientShortCode: activeClientShortName,
          };

      const token = window.localStorage.getItem("refreshtoken");

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      await axios
        .post(
          serviceAPIMapping["LOCATEMANAGEMENT-SERVICE"].apiRouteURL +
            EndPoint.getPreBorrows,
          JSON.stringify(request),
          { headers: headers }
        )
        .then(async (response: any) => {
          if (response?.status === 200) {
            const rows = await response?.data;
            if (rows && _.isArray(rows) && rows?.length > 0) {
              const sortedRows = _.orderBy(rows, "preborrows_id", "desc");
              setPreBorrowRows(sortedRows);
              setInitialPreBorrowRows(sortedRows);
              if (
                sortedRows &&
                _.isArray(sortedRows) &&
                !_.isEmpty(sortedRows)
              ) {
                if (isValidDate()) {
                  onRowSelect(sortedRows[0]);
                }
              }
            } else {
              setInitialPreBorrowRows([]);
              setPreBorrowRows([]);
            }
          } else if (response?.status === 401) {
            //logout(channel)
          } else {
            setPreBorrowRows([]);
            setInitialPreBorrowRows([]);
          }
        });
    } catch (err) {
      setPreBorrowRows([]);
      setInitialPreBorrowRows([]);
    } finally {
      setShowScreenLoader(false);
    }
  };

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit();
    gridApi = params.api;

    const gridColumnApi = params.columnApi;
    getName(gridApi, gridColumnApi);

    // Emmits the gridApi refrence to the parent component
    if (getGridApi) {
      getGridApi(gridApi, gridColumnApi);
    }
    getBtnRef(gridRef);
  };

  const getName = (api: any, columnApi: any) => {
    if (!api || !columnApi) return;
    const rows: any = {};
    const result = [];
    columnApi?.getAllGridColumns().forEach((item: any) => {
      rows[item.colId] = null;
    });
    const columnsWithAggregation = [
      "total_request_quantity",
      "rate",
      "value",
      "accrual",
    ];
    columnsWithAggregation.forEach((element) => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        if (rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]));
        }
      });
    });

    const columnsWithAvg = ["rate"];

    columnsWithAvg.forEach((element) => {
      const nodeCount = api.getDisplayedRowCount();
      const avg = (rows[element] / nodeCount).toString();
      if (nodeCount !== 0 && !_.isNaN(avg)) {
        rows[element] = Number(parseFloat(avg).toFixed(3));
      }
    });

    const contractIdTotal = ["preborrows_id"];
    let countContractId_ = 0;
    contractIdTotal.forEach((element) => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        countContractId_++;
        if (rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]));
        }
      });
    });
    rows["preborrows_id"] = convertToCommas(countContractId_);

    const count = Number(2);
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        result.push({
          cpty: "Total Clients",
          preborrows_id: "Total Rows",
          sec_id: "Total Tickers",
          total_request_quantity: "Total Req Qty",
          rate: "Avg Rate",
          value: "Total Value",
          accrual: "Total Accrual",
          isTotalRow: true,
          isTotalLabel: true,
        });
      } else {
        rows["isTotalRow"] = true;
        rows["sec_id"] = countUnique("sec_id", api);
        rows["cpty"] = countUnique("cpty", api);
        result.push(rows);
      }
    }
    api?.setPinnedBottomRowData(result);
  };

  /**
   * Method counts the unique value for given attribute (for filtered values also)
   * @param attribute
   * @returns unique values count
   */
  const countUnique = (attribute: string, api: GridApi): any => {
    const uniqueItemsSet: Set<any> = new Set();
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (rowNode?.data instanceof Object && attribute in rowNode.data) {
        uniqueItemsSet.add(rowNode.data[attribute]);
      }
    });

    return convertToCommas(uniqueItemsSet.size);
  };

  useEffect(() => {
    if (isDeselectAll != undefined) gridRef.current?.api?.deselectAll();
  }, [isDeselectAll]);

  const rowClassRules = useMemo(() => {
    return {
      "is-new-warning": (params: any) => {
        const isNew = params.data.is_new;

        return isNew === "TRUE";
      },
      "is-new-recall": (params: any) => {
        const isNew = params.data.is_Recall;

        return isNew === "True";
      },
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 100,
      maxWidth: 200,
    };
  }, []);

  const getRowId = (params: any) => {
    if (params && params.data) {
      if (params.data.preborrows_id) {
        return params.data.preborrows_id;
      } else if (params.data.id) {
        return params.data.id;
      }
    }

    return makeid(5);
  };

  function makeid(length: any) {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }

    return result;
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: "",
    };
  }, []);

  function isForceRefreshSelected() {
    return (document.querySelector("#forceRefresh") as HTMLInputElement)
      ?.checked;
  }
  function isSuppressFlashSelected() {
    return (document.querySelector("#suppressFlash") as HTMLInputElement)
      ?.checked;
  }
  const onGridRowDataChanged = () => {
    const refreshParams: any = {
      force: isForceRefreshSelected(),
      suppressFlash: isSuppressFlashSelected(),
    };
    gridRef.current!.api.refreshCells(refreshParams);
  };

  const onDoesfilter = useCallback((node: any) => {
    doesExternalFilterPass(node);

    return true;
  }, []);

  const onSelectionChanged = (params: any) => {
    const selectedRows = params?.api?.getSelectedRows();
    if (selectedRows && !_.isEmpty(selectedRows) && isValidDate()) {
      onRowSelect(selectedRows[0]);
    }
  };

  const isValidDate = () => {
    return validateTermDate(date);
  };

  const isRowSelectable = () => {
    return isValidDate();
  };

  const onFirstDataRendered = () => {
    let gridColumnApi = gridRef?.current!.columnApi;
    let gridRefApi = gridRef?.current!.api;
    getName(gridRefApi, gridColumnApi);
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const { api, columnApi } = params;
    getName(api, columnApi);
  };

  useEffect(() => {
    gridApi = gridRef.current!.api;
    var gridColumnApi = gridRef.current!.columnApi;
    getName(gridApi, gridColumnApi);
  }, [preBorrowRows]);

  const onRowClicked = (params: any) => {
    if (params?.data?.sec_id) {
      onRowSelect(params?.data);
    }
  };

  useEffect(() => {
    let returnInterval: any;
    if (enableSocketUpdates && !socketConsolidateClient?.connected) {
      socketConsolidateClient.connect(
        {},
        function (frame: any) {
          socketConsolidateClient.subscribe(
            "/topic/consolidated_message",
            async function asy(result: any) {
              let resultJson = JSON.parse(result?.body);
              if(isAdmin && getEnv() !== envs.PROD){
                console.log(
                  `Data received from /topic/consolidated_message at ${moment()?.toDate()} - `,
                  resultJson
                );
               }
             
              const orders = _.get(
                resultJson?.socketMssgmap,
                socketMsgType.ORDER
              );
              if (orders && _.isArray(orders) && orders?.length > 0) {
                await onSocketMsgReceive(orders);

                // if (queue?.size > 0) {
                //   queue.enqueue(orders);
                // } else {
                //   queue.enqueue(orders);
                //   const msg = queue?.dequeue();
                //   await onSocketMsgReceive(msg);
                // }
              }
            }
          );
        },
        {},
        () => {}
      );
    } else {
      if (currentInterval) {
        returnInterval = setInterval(() => {
          getPreBorrows()
        }, currentInterval);
        return () => clearInterval(returnInterval);
      }

      if (socketConsolidateClient?.connected) {
        socketConsolidateClient?.unsubscribe("/topic/consolidated_message");
        socketConsolidateClient?.disconnect();
      }
      // if (queue) {
      //   queue?.clear();
      // }
    }

    return () => {
      if (socketConsolidateClient?.connected) {
        socketConsolidateClient?.unsubscribe("/topic/consolidated_message");
        socketConsolidateClient?.disconnect();
      }
      // if (queue) {
      //   queue?.clear();
      // }
    };
  }, [enableSocketUpdates]);

  const onSocketMsgReceive = (orders: any) => {
    // const orders = _.get(msg, socketMsgType.ORDER);
    if (orders && _.isArray(orders) && orders?.length > 0) {
      let socketRows = [...orders]?.map((item: any) => {
        return item?.messageJson;
      });
      const locates = socketRows?.filter(
        (order: any) => order?.assetType === "Preborrow" && order?.username !== loginUser
      );
      if (locates && _.isArray(locates) && locates?.length > 0) {
        setPreBorrowRows((previousRows: any) => {
          const currentRows = [...previousRows];
          const updates = _.intersectionBy(
            locates,
            previousRows,
            "preborrows_id"
          );
          const inserts = _.differenceBy(
            locates,
            previousRows,
            "preborrows_id"
          );
          if (_.isArray(inserts) && inserts?.length > 0) {
            setInsertPreborrowRows(inserts);
          }

          updates?.forEach((locate: any) => {
            const index = currentRows?.findIndex(
              (loc) => loc?.preborrows_id === locate?.preborrows_id
            );
            if (index >= 0) {
              currentRows[index] = locate;
            }
          });

          return currentRows;
        });

        // setTimeout(() => {
        //   if (queue?.size > 0) {
        //     const msg = queue?.dequeue();
        //     onSocketMsgReceive(msg);
        //   }
        // }, 100);
      }
    }
  };

  useEffect(() => {
    let rows = [...initialPreBorrowRows];
    if (rows && _.isArray(rows) && !_.isEmpty(rows)) {
      if (globalClient && !_.isEmpty(globalClient) && globalClient !== "all") {
        rows = rows?.filter((row: any) => row?.cpty === globalClient);
      }

      setPreBorrowRows(rows);
    }
  }, [initialPreBorrowRows, globalClient]);

  useEffect(() => {
    if (
      insertPreBorrowRows &&
      _.isArray(insertPreBorrowRows) &&
      !_.isEmpty(insertPreBorrowRows)
    ) {
      // setPreBorrowRows((previousRows: any) => {
      //   const currentRows = [...insertPreBorrowRows, ...previousRows];
      //   const uniqRows = _.uniqBy(currentRows, "preborrows_id");

      //   return uniqRows;
      // });

      setInitialPreBorrowRows((previousRows: any) => {
        const currentRows = [...insertPreBorrowRows, ...previousRows];
        const uniqRows = _.uniqBy(currentRows, "preborrows_id");

        return uniqRows;
      });
    }
  }, [insertPreBorrowRows]);

  return (
    <div
      className={"h-100" + gridTheme}
      style={{
        width: "100%",
      }}
    >
      {title !== undefined && (
        <div className="drag-handle title-subHeaderTitle"> {title} </div>
      )}
      {showScreenLoader && <SpinnerFullScreen></SpinnerFullScreen>}
      <AgGridReact
        ref={gridRef}
        defaultColDef={defaultColDef}
        rowStyle={{
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: "400",
          fontStyle: "normal",
        }}
        headerHeight={26}
        rowHeight={26}
        columnDefs={columnDefs}
        getRowId={getRowId}
        onRowDataUpdated={onGridRowDataChanged}
        rowData={preBorrowRows}
        suppressRowClickSelection={false}
        suppressAggFuncInHeader={true}
        enableRangeSelection={true}
        onGridReady={onGridReady}
        rowClassRules={rowClassRules}
        autoGroupColumnDef={autoGroupColumnDef}
        groupSelectsChildren={true}
        groupSelectsFiltered={true}
        sideBar={sideBar}
        animateRows={true}
        pivotMode={false}
        cacheQuickFilter={true}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={onDoesfilter}
        getContextMenuItems={getContextMenuItems}
        quickFilterText={globalSearch}
        overlayLoadingTemplate={
          '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
        }
        onSelectionChanged={onSelectionChanged}
        rowSelection={"single"}
        isRowSelectable={isRowSelectable}
        onFilterChanged={onFilterChanged}
        onFirstDataRendered={onFirstDataRendered}
        getRowStyle={getRowStyle}
        onRowClicked={onRowClicked}
      ></AgGridReact>
    </div>
  );
};

export default PreBorrowGrid;
